import { jsPDF } from 'jspdf';
import html2pdf from 'html2pdf.js';
import RobotoSlabRegular from '../fonts/RobotoSlab-Regular.ttf';
import RobotoSlabBold from '../fonts/RobotoSlab-Bold.ttf';

/**
 * Gera PDF de Procuração no padrão CEMIG WG SOL usando HTML2PDF
 * @param {Object} dados - Dados da procuração
 * @returns {Promise<Blob>} Blob do PDF gerado
 */
export const gerarProcuracaoPDF = async (dados) => {
    // Validações básicas
    if (!dados.nome || !dados.cpf_cnpj || !dados.municipio || !dados.uf) {
        throw new Error('Dados obrigatórios ausentes');
    }

    // Preparar dados formatados
    const pj = dados.tipo === 'PJ';
    const cpfFormatado = pj ? formatarCNPJ(dados.cpf_cnpj) : formatarCPF(dados.cpf_cnpj);
    
    // Montar endereço
    let endereco = '';
    if (dados.logradouro) {
        const partes = [dados.logradouro];
        if (dados.numero) partes.push(dados.numero);
        if (dados.complemento) partes.push(dados.complemento);
        endereco = partes.join(', ');
        if (dados.bairro) endereco += `, ${dados.bairro}`;
        endereco += `, ${dados.municipio} - ${dados.uf}`;
        if (dados.cep) endereco += `, ${formatarCEP(dados.cep)}`;
    } else {
        endereco = `${dados.municipio} - ${dados.uf}`;
    }

    const preposicao = dados.logradouro ? 'na' : 'em';

    // Dados de assinatura
    const data = dados.data ? new Date(dados.data) : new Date();
    const dataFormatada = data.toLocaleDateString('pt-BR');
    const cidade = (dados.cidade || 'Betim').toUpperCase();

    // Montar texto OUTORGANTE
    let outorganteText = '';
    if (pj) {
        const rgRep = dados.rep_rg ? `IDENTIDADE ${dados.rep_rg}, ` : '';
        outorganteText = `${dados.rep_nome}, brasileiro(a), CPF ${formatarCPF(dados.rep_cpf)}, ${rgRep}representando a empresa ${dados.nome}, regida pelo CNPJ ${cpfFormatado} localizada ${preposicao} ${endereco}`;
    } else {
        const rg = dados.rg ? `IDENTIDADE ${dados.rg}, ` : '';
        outorganteText = `${dados.nome}, brasileiro(a), CPF ${cpfFormatado}, ${rg}residido(a) ${preposicao} ${endereco}`;
    }

    // Importar logo
    let logoBase64 = '';
    try {
        const logoUrl = require('../images/logo.png');
        // Converter para data URL se for possível
        const img = new Image();
        img.src = logoUrl;
        // Usar o logoUrl diretamente como src em img tag
        logoBase64 = logoUrl;
    } catch (e) {
        console.warn('Logo não encontrada');
    }

    // Criar HTML com rodapé fixo na base
    const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="UTF-8">
        
        <style>
            @font-face {
                font-family: 'Roboto Slab';
                src: url('${RobotoSlabRegular}') format('truetype');
                font-weight: normal;
            }
            @font-face {
                font-family: 'Roboto Slab';
                src: url('${RobotoSlabBold}') format('truetype');
                font-weight: bold;
            }
            * {
                margin: 0;
                padding: 0;
            }
            body {
                font-size: 11px;
                line-height: 1.6;
                color: #000;
                padding: 0;
                margin: 0;
            }
            .page {
                width: 210mm;
                min-height: 296mm;
                margin: 0 auto;
                position: relative;
                padding: 20mm 15mm 30mm 15mm;
                box-sizing: border-box;
                background: white;
                page-break-after: always;
            }
            .logo-container {
                text-align: center;
                margin-bottom: 10px;
                padding-bottom: 8px;
            }
            .logo-container img {
                max-width: 80mm;
                height: auto;
            }
            .title {
                font-weight: bold;
                font-size: 16px;
                margin: 15px 0;
                text-align: center;
            }
            .section {
                margin-bottom: 7.8mm;
                text-align: justify;
                
            }
            .section-title {
                font-weight: bold;
                display: inline;
            }
            strong {
                font-weight: bold;
            }
            .signature-line {
                margin-top: 20px;
                text-align: center;
            }
            .line {
                border-top: 1px dashed #000;
                width: 60mm;
                margin: 0 auto 4px;
            }
            .signature-data {
                margin-top: 5px;
                text-align: center;
                font-size: 12px;
            }
            .date-location {
                text-align: left;
                margin: 10px 0;

                margin-bottom: 100px;
            }
            .spacer {
                flex: 1;
            }
            .footer {
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                width: 100%;
                padding: 5mm 15mm;
                text-align: center;
                font-size: 12px;
                background: white;
            }
        </style>
    </head>
    <body>
        <div class="page">
            ${logoBase64 ? `<div class="logo-container"><img src="${logoBase64}" alt="Logo"></div>` : ''}
            
            <div class="title">
                <div style="border-bottom: 1px solid #000; padding-bottom: 0px; display: inline-block;">
                    INSTRUMENTO PARTICULAR DE PROCURAÇÃO
                </div>
            </div>

            <div class="section">
                <span class="section-title">OUTORGANTE:</span> ${outorganteText}
            </div>

            <div class="section">
                <span class="section-title">OUTORGADO:</span> Rodrigo Dutra Schuchmann, brasileiro, Engenheiro Eletricista, casado, CPF 013.605.966-07, CREA: MG0000208488D, residente na Av. das Américas, 872 - Centro, Betim - MG, 32600-170.
            </div>

            <div class="section">
                Pelo presente instrumento particular de procuração, o <strong>OUTORGANTE</strong> supra, nomeia e constitui seu bastante procurador o <strong>OUTORGADO</strong> acima, ao qual concede os poderes para fins específicos de representá-lo(s) perante a <strong>CEMIG DISTRIBUIÇÃO S.A</strong>, sociedade por ações, constituída como subsidiária integral da Companhia Energética de Minas Gerais, inscrita no CNPJ/MF sob o nº 06.981.180/0001-16, com sede em Belo Horizonte – MG, na Av. Barbacena, 1200, 17º andar – A1, Bairro Santo Agostinho, doravante denominada <strong>CEMIG D</strong>, para fins de efetuar <strong>a troca de titularidade da instalação</strong>, bem como promover a execução de obra em rede de distribuição para o <strong>OUTORGANTE</strong>, sob projeto CEMIG, podendo o <strong>OUTORGADO</strong> efetuar a assinatura da ART- Anotação de Responsabilidade Técnica da referida obra, representando o <strong>OUTORGANTE</strong>.
            </div>

            <div class="date-location">
                ${cidade}, ${dataFormatada}
            </div>

            <div class="signature-line">
                <div class="line"></div>
                <div class="signature-data">
                    ${pj ? `${dados.rep_nome}<br>CPF ${formatarCPF(dados.rep_cpf)}<br>CNPJ ${cpfFormatado}` : `${dados.nome}<br>CPF ${cpfFormatado}${dados.rg ? `<br>ID ${dados.rg}` : ''}`}
                </div>
            </div>

            <div class="footer">
                Endereço: Av. das Américas, 872 - Centro, Betim - MG, 32600-170 / Telefone: (31) 3532-3574
            </div>
        </div>
    </body>
    </html>
    `;

    // Criar elemento temporário
    const elemento = document.createElement('div');
    elemento.innerHTML = htmlContent;
    document.body.appendChild(elemento);

    // Gerar PDF com html2pdf
    try {
        const config = {
            margin: 0,
            filename: 'procuracao.pdf',
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, logging: false, useCORS: true },
            jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' },
            pagebreak: { mode: 'avoid-all' }
        };

        const blob = await html2pdf().set(config).from(elemento).output('blob');
        return blob;
    } finally {
        document.body.removeChild(elemento);
    }
};

/**
 * Funções auxiliares de formatação
 */

const soDigitos = (s) => (s || '').replace(/\D/g, '');

export const formatarCPF = (s) => {
    const n = soDigitos(s);
    return n.length === 11 
        ? `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6, 9)}-${n.slice(9)}`
        : s;
};

export const formatarCNPJ = (s) => {
    const n = soDigitos(s);
    return n.length === 14 
        ? `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5, 8)}/${n.slice(8, 12)}-${n.slice(12)}`
        : s;
};

export const formatarCEP = (s) => {
    const n = soDigitos(s);
    return n.length === 8 
        ? `${n.slice(0, 5)}-${n.slice(5)}`
        : s;
};

/**
 * Validações
 */

const validaCPF = (s) => {
    const n = soDigitos(s);
    if (n.length !== 11 || n === n[0].repeat(11)) return false;
    
    for (let k = 9; k <= 10; k++) {
        let soma = 0;
        for (let i = 0; i < k; i++) {
            soma += parseInt(n[i]) * (k + 1 - i);
        }
        if ((soma * 10) % 11 % 10 !== parseInt(n[k])) return false;
    }
    return true;
};

const validaCNPJ = (s) => {
    const n = soDigitos(s);
    if (n.length !== 14 || n === n[0].repeat(14)) return false;
    
    const pesos1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const pesos2 = [6, ...pesos1];
    
    for (let idx = 0; idx < 2; idx++) {
        const pesos = idx === 0 ? pesos1 : pesos2;
        const k = idx === 0 ? 12 : 13;
        let soma = 0;
        for (let i = 0; i < pesos.length; i++) {
            soma += parseInt(n[i]) * pesos[i];
        }
        const dv = 11 - (soma % 11);
        if ((dv > 9 ? 0 : dv) !== parseInt(n[k])) return false;
    }
    return true;
};

const validarData = (dataStr) => {
    if (!dataStr) return true;
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dataStr)) return false;
    
    try {
        const [ano, mes, dia] = dataStr.split('-').map(Number);
        return !isNaN(new Date(ano, mes - 1, dia).getTime());
    } catch {
        return false;
    }
};

/**
 * Validação completa dos dados
 */
export const validarDados = (dados) => {
    const erros = [];
    const pj = dados.tipo === 'PJ';

    if (!dados.tipo || !['PF', 'PJ'].includes(dados.tipo)) {
        erros.push('Tipo deve ser "PF" ou "PJ"');
    }

    if (!dados.nome?.trim()) {
        erros.push('Campo obrigatório ausente: nome');
    }

    if (!dados.cpf_cnpj?.trim()) {
        erros.push('Campo obrigatório ausente: CPF/CNPJ');
    } else {
        if (pj) {
            if (!validaCNPJ(dados.cpf_cnpj)) {
                erros.push('CNPJ inválido (dígitos verificadores)');
            }
        } else {
            if (!validaCPF(dados.cpf_cnpj)) {
                erros.push('CPF inválido (dígitos verificadores)');
            }
        }
    }

    if (!dados.municipio?.trim()) {
        erros.push('Campo obrigatório ausente: município');
    }

    if (!dados.uf?.trim()) {
        erros.push('Campo obrigatório ausente: UF');
    } else if (!/^[A-Z]{2}$/.test(dados.uf.toUpperCase())) {
        erros.push('UF deve ser uma sigla de 2 letras');
    }

    if (pj) {
        if (!dados.rep_nome?.trim()) {
            erros.push('Campo obrigatório ausente (PJ): rep_nome');
        }
        if (!dados.rep_cpf?.trim()) {
            erros.push('Campo obrigatório ausente (PJ): rep_cpf');
        } else if (!validaCPF(dados.rep_cpf)) {
            erros.push('CPF do representante inválido (dígitos verificadores)');
        }
    }

    if (dados.data) {
        const dataISO = dados.data instanceof Date 
            ? dados.data.toISOString().split('T')[0]
            : dados.data;
        if (!validarData(dataISO)) {
            erros.push('Data inválida (use formato AAAA-MM-DD e data existente)');
        }
    }

    return erros;
};
